import type { PulseChart } from '../chart.js';
import type { ReplayInputs } from '../replay.js';
import type { Variant } from '../gameTypes.js';
import { VARIANTS, buildVariantSlug } from '../gameTypes.js';
import { buildVariantTitle } from '../chart.js';
import { ChartBuilder, expectedReplayFromPads } from '../engine/pulse/chartBuilder.js';
import { PALETTE_AMBER } from './palettes.js';
import {
  buildBuiltinAudio,
  findBurstChambers,
  mergeBeats,
  variantSelectBeats,
} from './audioFromOnsets.js';
import onsets from './audio/cradles.onsets.json' with { type: 'json' };

const audio = buildBuiltinAudio({
  src: '/audio/tracks/cradles.mp3',
  attribution: '"Cradles" by Sub Urban [NCS Release] - https://ncs.io/Cradles',
  data: onsets,
});

function buildChart(variant: Variant): PulseChart {
  const builder = new ChartBuilder({
    id: buildVariantSlug('cradles', variant),
    slug: buildVariantSlug('cradles', variant),
    title: buildVariantTitle('Cradles', variant),
    description:
      'Dark alt-pop, 95 BPM. Vocal-led verses with snappy snare hits in the chorus and a melodic bridge.',
    bpm: onsets.bpm,
    difficulty: 3,
    variant,
    spawnSurface: 'floor',
    palette: PALETTE_AMBER,
    audio,
  });

  // 60s @ 95 BPM ≈ 95 beats. Cradles has very few kicks (20) but lots of
  // vocal/lead onsets (203) - the rhythm is largely vocal-driven.
  builder.section({ startBeat: 0, endBeat: 32, kind: 'verse' });
  builder.section({ startBeat: 32, endBeat: 48, kind: 'chorus' });
  builder.section({ startBeat: 48, endBeat: 64, kind: 'bridge' });
  builder.section({ startBeat: 64, endBeat: 95, kind: 'chorus' });

  // Verse: vocal-led. Snare hits land on the "I was scared..." consonants.
  const versePads = variantSelectBeats(variant, onsets, {
    fromBeat: 4,
    untilBeat: 32,
    kinds: ['snare', 'lead'],
    minIntensity: 0.4,
    minSpacing: 1.5,
  });

  // Chorus: snare backbeat + heavier vocals. Higher density than verse.
  const chorusPads = variantSelectBeats(variant, onsets, {
    fromBeat: 32,
    untilBeat: 48,
    kinds: ['snare', 'lead'],
    minIntensity: 0.3,
    minSpacing: 0.75,
  });

  // Bridge: melodic lead only - give the player a quieter moment.
  const bridgePads = variantSelectBeats(variant, onsets, {
    fromBeat: 48,
    untilBeat: 64,
    kinds: ['lead'],
    minIntensity: 0.4,
    minSpacing: 1.5,
  });

  // Final chorus: pull every kind in for the climax. The tempo is slow
  // enough that 0.5-beat spacing is still readable.
  const finalPads = variantSelectBeats(variant, onsets, {
    fromBeat: 64,
    untilBeat: 93,
    kinds: ['kick', 'snare', 'lead'],
    minIntensity: 0.3,
    minSpacing: 0.5,
  });

  const padBeats = mergeBeats(versePads, chorusPads, bridgePads, finalPads);
  for (const beat of padBeats) {
    builder.pad(beat);
  }

  // Cradles is sparse - vocal-led with long instrumental gaps. Slightly
  // lower minGap so even shorter quiet stretches get a chamber.
  for (const c of findBurstChambers(padBeats, {
    fromBeat: 4,
    untilBeat: 93,
    minGapBeats: 5,
  })) {
    builder.burstChamber(c);
  }

  builder.setTotalBeats(95);
  return builder.build();
}

export const CRADLES_VARIANTS: Record<Variant, PulseChart> = Object.fromEntries(
  VARIANTS.map((v) => [v, buildChart(v)]),
) as Record<Variant, PulseChart>;

export const CRADLES_EXPECTED_REPLAYS: Record<Variant, ReplayInputs> =
  Object.fromEntries(
    VARIANTS.map((v) => [v, expectedReplayFromPads(CRADLES_VARIANTS[v])]),
  ) as Record<Variant, ReplayInputs>;
